/**
 * Gestión de Notas de Crédito de compra (Iter 236.r, Feb 2026, Carlos).
 *
 * Lista, linkea y marca como aplicadas las Notas de Crédito (DTE tipo 61) que
 * recibimos de proveedores. Permite asociar una NC a su factura original
 * (auto desde el XML o manual desde el listado de sugerencias).
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  RefreshCw,
  Link2,
  Unlink,
  CheckCircle2,
  Undo2,
  ExternalLink,
  AlertTriangle,
  FileMinus,
  FileText,
  Search,
  Loader2,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { notasCreditoApi } from '../../lib/api';
import { TableSkeleton } from '../../lib/skeletons';
// Iter 292.g — helper canónico DD-MM-YYYY (alias local `fmtDate`).
import { fmtFecha as fmtDate } from '../../lib/utils';
import NotasCreditoVentaTab from './NotasCreditoVentaTab';

const fmtCLP = (n) => `$${(Number(n) || 0).toLocaleString('es-CL')}`;

const CATEGORIA_META = {
  sin_link: {
    label: 'Sin linkear',
    cls: 'bg-amber-100 text-amber-800 border-amber-300',
    desc: 'NC sin referencia al folio original — requiere linkeo manual',
    icon: '⚠️',
  },
  link_xml: {
    label: 'Linkeada (XML)',
    cls: 'bg-blue-100 text-blue-800 border-blue-300',
    desc: 'Auto-linkeada desde la referencia del XML',
    icon: '🔗',
  },
  link_manual: {
    label: 'Linkeada (manual)',
    cls: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    desc: 'Linkeada manualmente por un usuario',
    icon: '✋',
  },
  aplicada: {
    label: 'Aplicada',
    cls: 'bg-green-100 text-green-800 border-green-300',
    desc: 'NC ya aplicada contablemente',
    icon: '✓',
  },
  sin_factura_original: {
    label: 'Factura original no existe',
    cls: 'bg-red-100 text-red-800 border-red-300',
    desc: 'XML referencia un folio que NO está en el sistema',
    icon: '🚫',
  },
};

export default function NotasCreditoPage() {
  const [mainTab, setMainTab] = useState('compra');
  const [tab, setTab] = useState('sin_link');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, kpis: {} });
  const [proveedorFiltro, setProveedorFiltro] = useState('');
  const [refreshSpin, setRefreshSpin] = useState(false);

  // Dialog: linkear manual
  const [linkDlg, setLinkDlg] = useState(null); // {nc, sugerencias}
  const [linkSearchTerm, setLinkSearchTerm] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [submittingLink, setSubmittingLink] = useState(false);

  // Dialog: marcar como aplicada
  const [aplicarDlg, setAplicarDlg] = useState(null); // {nc}
  const [motivo, setMotivo] = useState('');
  const [alegraId, setAlegraId] = useState('');
  const [aplicarBusy, setAplicarBusy] = useState(false);
  // Sub-flujo: rebajar en Alegra (Nota de Débito)
  const [previewAlegra, setPreviewAlegra] = useState(null); // dry-run result
  const [previewBusy, setPreviewBusy] = useState(false);
  const [alegraBusy, setAlegraBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days_back: 365 };
      if (proveedorFiltro.trim()) params.proveedor = proveedorFiltro.trim();
      const r = await notasCreditoApi.list(params);
      setData(r.data || { items: [], total: 0, kpis: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando notas de crédito');
    } finally {
      setLoading(false);
    }
  }, [proveedorFiltro]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filteredItems = useMemo(
    () => (data.items || []).filter((it) => it.categoria === tab),
    [data.items, tab],
  );

  const kpis = data.kpis || {};

  // ───────────────────── Acciones ─────────────────────

  const handleAutoLink = async () => {
    setRefreshSpin(true);
    try {
      const r = await notasCreditoApi.autoLinkearBatch();
      const d = r.data || {};
      toast.success(
        `Auto-link completado: ${d.linked_count} linkeadas, ${d.already_linked} ya estaban, ${d.skipped_no_match} sin factura original`,
      );
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en auto-link batch');
    } finally {
      setRefreshSpin(false);
    }
  };

  const openLinkDialog = async (nc) => {
    setLinkDlg({ nc, sugerencias: [] });
    setLinkSearchTerm('');
    setLinkLoading(true);
    try {
      const r = await notasCreditoApi.sugerencias(nc.folio);
      setLinkDlg({ nc, sugerencias: r.data?.sugerencias || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando sugerencias');
    } finally {
      setLinkLoading(false);
    }
  };

  const submitLink = async (factura_folio) => {
    if (!factura_folio || !linkDlg?.nc) return;
    setSubmittingLink(true);
    try {
      await notasCreditoApi.linkear(linkDlg.nc.folio, factura_folio);
      toast.success(`NC ${linkDlg.nc.folio} linkeada a factura ${factura_folio}`);
      setLinkDlg(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error linkeando');
    } finally {
      setSubmittingLink(false);
    }
  };

  const quitarLink = async (folio) => {
    if (!window.confirm(`¿Quitar el link manual de la NC ${folio}?`)) return;
    try {
      await notasCreditoApi.quitarLink(folio);
      toast.success(`Link quitado de NC ${folio}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error quitando link');
    }
  };

  const openAplicarDialog = (nc) => {
    setMotivo('');
    setAlegraId(nc.nc_alegra_credit_note_id || '');
    setPreviewAlegra(null);
    setAplicarDlg({ nc });
  };

  const previsualizarAlegra = async () => {
    if (!aplicarDlg?.nc) return;
    setPreviewBusy(true);
    try {
      const r = await notasCreditoApi.aplicarAlegra(aplicarDlg.nc.folio, {
        motivo: motivo.trim(),
        dry_run: true,
      });
      setPreviewAlegra(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al previsualizar en Alegra');
      setPreviewAlegra(null);
    } finally {
      setPreviewBusy(false);
    }
  };

  const aplicarEnAlegra = async () => {
    if (!aplicarDlg?.nc) return;
    if (motivo.trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setAlegraBusy(true);
    try {
      const r = await notasCreditoApi.aplicarAlegra(aplicarDlg.nc.folio, {
        motivo: motivo.trim(),
        dry_run: false,
      });
      toast.success(r.data?.mensaje || `NC ${aplicarDlg.nc.folio} aplicada en Alegra`);
      setAplicarDlg(null);
      setPreviewAlegra(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al aplicar en Alegra');
    } finally {
      setAlegraBusy(false);
    }
  };

  const submitAplicar = async () => {
    if (!aplicarDlg?.nc) return;
    if (motivo.trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setAplicarBusy(true);
    try {
      await notasCreditoApi.marcarAplicada(
        aplicarDlg.nc.folio,
        motivo.trim(),
        alegraId.trim() || null,
      );
      toast.success(`NC ${aplicarDlg.nc.folio} marcada como aplicada`);
      setAplicarDlg(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error marcando aplicada');
    } finally {
      setAplicarBusy(false);
    }
  };

  const revertirAplicacion = async (folio) => {
    if (
      !window.confirm(
        `¿Revertir la aplicación de la NC ${folio}? Se podrá volver a aplicar después.`,
      )
    )
      return;
    try {
      await notasCreditoApi.revertirAplicacion(folio);
      toast.success(`Aplicación revertida (NC ${folio})`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error revirtiendo');
    }
  };

  // Filtrado sobre sugerencias (búsqueda local en el dialog)
  const sugerenciasFiltradas = useMemo(() => {
    if (!linkDlg?.sugerencias) return [];
    const q = linkSearchTerm.trim().toLowerCase();
    if (!q) return linkDlg.sugerencias;
    return linkDlg.sugerencias.filter(
      (s) =>
        String(s.folio || '').toLowerCase().includes(q) ||
        String(s.total || '').includes(q) ||
        String(s.fecha_emision || '').includes(q),
    );
  }, [linkDlg, linkSearchTerm]);

  return (
    <div className="space-y-6 p-6" data-testid="notas-credito-page">
      <h1 className="text-3xl font-bold flex items-center gap-2">
        <FileMinus className="w-7 h-7 text-orange-600" />
        Notas de Crédito
      </h1>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="grid grid-cols-2 max-w-md">
          <TabsTrigger value="compra" data-testid="tab-nc-compra">
            Compra (proveedores)
          </TabsTrigger>
          <TabsTrigger value="venta" data-testid="tab-nc-venta">
            Venta (CHIRINO)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compra" className="mt-4 space-y-6">
      {/* ───────── Header ───────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-gray-600 max-w-2xl">
            Gestión de NCs recibidas de proveedores (DTE tipo 61). Linkea cada NC a su
            factura original y marca como aplicada cuando se haya descontado en Alegra.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filtrar proveedor (nombre/RUT)…"
            value={proveedorFiltro}
            onChange={(e) => setProveedorFiltro(e.target.value)}
            className="w-64"
            data-testid="nc-proveedor-filter"
          />
          <Button
            onClick={reload}
            variant="outline"
            disabled={loading}
            data-testid="nc-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Recargar
          </Button>
          <Button
            onClick={handleAutoLink}
            variant="default"
            disabled={refreshSpin}
            data-testid="nc-auto-link-batch-btn"
            title="Linkea automáticamente todas las NCs con referencia XML válida"
          >
            <Zap className={`w-4 h-4 mr-2 ${refreshSpin ? 'animate-pulse' : ''}`} />
            Auto-link batch
          </Button>
        </div>
      </div>

      {/* ───────── KPIs ───────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard label="Total NCs" value={kpis.total || 0} tone="gray" />
        <KpiCard
          label="Sin linkear"
          value={kpis.sin_link || 0}
          tone="amber"
          testid="kpi-sin-link"
        />
        <KpiCard label="Link XML" value={kpis.link_xml || 0} tone="blue" />
        <KpiCard label="Link manual" value={kpis.link_manual || 0} tone="indigo" />
        <KpiCard label="Aplicadas" value={kpis.aplicada || 0} tone="green" />
        <KpiCard
          label="Monto pendiente"
          value={fmtCLP(kpis.monto_pendiente_total || 0)}
          tone="red"
          numeric={false}
          testid="kpi-monto-pendiente"
        />
      </div>

      {/* ───────── Tabs ───────── */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="sin_link" data-testid="tab-sin-link">
            Sin link ({kpis.sin_link || 0})
          </TabsTrigger>
          <TabsTrigger value="link_xml" data-testid="tab-link-xml">
            Auto ({kpis.link_xml || 0})
          </TabsTrigger>
          <TabsTrigger value="link_manual" data-testid="tab-link-manual">
            Manual ({kpis.link_manual || 0})
          </TabsTrigger>
          <TabsTrigger value="aplicada" data-testid="tab-aplicada">
            Aplicadas ({kpis.aplicada || 0})
          </TabsTrigger>
          <TabsTrigger value="sin_factura_original" data-testid="tab-sin-factura">
            Sin fact. orig. ({kpis.sin_factura_original || 0})
          </TabsTrigger>
        </TabsList>

        {['sin_link', 'link_xml', 'link_manual', 'aplicada', 'sin_factura_original'].map(
          (cat) => (
            <TabsContent key={cat} value={cat} className="mt-4">
              <CategoryHelp cat={cat} />
              {loading ? (
                <TableSkeleton rows={8} cols={7} />
              ) : (
                <NCTable
                  items={filteredItems}
                  onLink={openLinkDialog}
                  onQuitarLink={quitarLink}
                  onAplicar={openAplicarDialog}
                  onRevertir={revertirAplicacion}
                />
              )}
            </TabsContent>
          ),
        )}
      </Tabs>

      {/* ───────── Dialog: Linkear ───────── */}
      <Dialog open={!!linkDlg} onOpenChange={(o) => !o && setLinkDlg(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Linkear NC a factura original</DialogTitle>
            <DialogDescription>
              {linkDlg && (
                <span>
                  NC <b>{linkDlg.nc.folio}</b> de{' '}
                  <b>{linkDlg.nc.emisor_razon_social}</b> por <b>{fmtCLP(linkDlg.nc.total)}</b>{' '}
                  · {fmtDate(linkDlg.nc.fecha_emision)}
                  {linkDlg.nc.razon_xml && (
                    <span className="block text-xs italic mt-1">
                      Razón XML: «{linkDlg.nc.razon_xml}»
                    </span>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 mb-2">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar folio / monto / fecha…"
              value={linkSearchTerm}
              onChange={(e) => setLinkSearchTerm(e.target.value)}
              data-testid="nc-link-search"
            />
          </div>

          {linkLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Cargando sugerencias…</span>
            </div>
          ) : sugerenciasFiltradas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay sugerencias del mismo proveedor en los últimos 365 días.
              <p className="text-xs mt-2">
                Puedes ingresar el folio manualmente abajo si lo conoces.
              </p>
            </div>
          ) : (
            <div className="border rounded max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Folio</th>
                    <th className="text-left px-2 py-1">Fecha</th>
                    <th className="text-right px-2 py-1">Total</th>
                    <th className="text-right px-2 py-1">Δ NC</th>
                    <th className="text-left px-2 py-1">OC</th>
                    <th className="text-center px-2 py-1">Match</th>
                    <th className="text-center px-2 py-1">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {sugerenciasFiltradas.map((s) => (
                    <tr
                      key={s.folio + '_' + (s.source || '')}
                      className="border-t hover:bg-blue-50"
                    >
                      <td className="px-2 py-1 font-mono">{s.folio}</td>
                      <td className="px-2 py-1 text-gray-600">{fmtDate(s.fecha_emision)}</td>
                      <td className="px-2 py-1 text-right">{fmtCLP(s.total)}</td>
                      <td className="px-2 py-1 text-right text-gray-500">
                        {fmtCLP(s.diff_monto)}
                      </td>
                      <td className="px-2 py-1 text-xs text-gray-500">
                        {s.orden_compra || '—'}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {s.match_exacto_monto && (
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            Exacto
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => submitLink(s.folio)}
                          disabled={submittingLink}
                          data-testid={`nc-link-pick-${s.folio}`}
                        >
                          Linkear
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t pt-3 mt-2">
            <Label className="text-xs">¿No aparece la factura? Ingresa folio manual:</Label>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="Ej: 123456"
                value={linkSearchTerm}
                onChange={(e) => setLinkSearchTerm(e.target.value)}
                data-testid="nc-link-folio-manual"
              />
              <Button
                onClick={() => submitLink(linkSearchTerm.trim())}
                disabled={!linkSearchTerm.trim() || submittingLink}
                variant="outline"
              >
                Forzar link
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDlg(null)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ───────── Dialog: Marcar Aplicada ───────── */}
      <Dialog open={!!aplicarDlg} onOpenChange={(o) => !o && setAplicarDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Marcar NC como aplicada</DialogTitle>
            <DialogDescription>
              {aplicarDlg && (
                <span>
                  NC <b>{aplicarDlg.nc.folio}</b> · {aplicarDlg.nc.emisor_razon_social} ·{' '}
                  <b>{fmtCLP(aplicarDlg.nc.total)}</b>
                  {aplicarDlg.nc.folio_factura_ref && (
                    <span className="block text-xs mt-1">
                      Se aplicará a factura <b>{aplicarDlg.nc.folio_factura_ref}</b>
                    </span>
                  )}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-sm">
                Motivo / Observación <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej: NC por mayor valor indicado en OC — rebaja factura 4154"
                rows={2}
                data-testid="nc-aplicar-motivo"
              />
              <p className="text-xs text-gray-500 mt-1">Mínimo 5 caracteres (auditoría)</p>
            </div>

            {/* ───── Opción recomendada: rebajar en Alegra ───── */}
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                <FileMinus className="w-4 h-4" />
                Rebajar en Alegra (Nota de Débito)
              </div>
              <p className="text-xs text-emerald-700">
                Crea una <b>Nota de Débito</b> en Alegra contra la factura asociada,
                descontando su saldo, y refleja el cambio en CxP/Nómina y en la OC. La NC
                queda contabilizada automáticamente.
              </p>

              <Button
                variant="outline"
                size="sm"
                onClick={previsualizarAlegra}
                disabled={previewBusy || alegraBusy}
                data-testid="nc-preview-alegra-btn"
              >
                {previewBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Previsualizar
              </Button>

              {previewAlegra && (
                <div
                  className="bg-white border rounded p-2 text-xs space-y-1"
                  data-testid="nc-preview-alegra-result"
                >
                  <div className="flex justify-between">
                    <span className="text-gray-500">Factura</span>
                    <span className="font-mono">{previewAlegra.factura_folio}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Monto NC</span>
                    <span className="font-semibold text-orange-700">
                      {fmtCLP(previewAlegra.total)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Saldo factura actual</span>
                    <span>{fmtCLP(previewAlegra.saldo_factura_actual)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="text-gray-600 font-medium">Saldo factura proyectado</span>
                    <span className="font-bold text-emerald-700">
                      {fmtCLP(previewAlegra.saldo_factura_proyectado)}
                    </span>
                  </div>
                  {previewAlegra.advertencia_sobregiro && (
                    <div className="text-red-600 flex items-center gap-1 pt-1">
                      <AlertTriangle className="w-3 h-3" />
                      La NC supera el saldo de la factura.
                    </div>
                  )}
                  {previewAlegra.ya_tiene_debit_note && (
                    <div className="text-amber-700 flex items-center gap-1 pt-1">
                      <AlertTriangle className="w-3 h-3" />
                      Esta NC ya tiene una Nota de Débito en Alegra (
                      {previewAlegra.debit_note_id}). No se duplicará.
                    </div>
                  )}
                </div>
              )}

              <Button
                size="sm"
                onClick={aplicarEnAlegra}
                disabled={alegraBusy || motivo.trim().length < 5}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="nc-aplicar-alegra-submit"
              >
                {alegraBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Aplicar y rebajar en Alegra
              </Button>
            </div>

            {/* ───── Opción alternativa: solo local ───── */}
            <details className="border rounded p-2 text-xs">
              <summary className="cursor-pointer text-gray-600 font-medium">
                Marcar como aplicada solo en el ERP (sin tocar Alegra)
              </summary>
              <div className="mt-2 space-y-2">
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  No crea la Nota de Débito en Alegra. Úsalo solo si ya la registraste a mano en Alegra.
                </div>
                <div>
                  <Label className="text-xs">ID en Alegra (opcional)</Label>
                  <Input
                    value={alegraId}
                    onChange={(e) => setAlegraId(e.target.value)}
                    placeholder="Ej: 7892"
                    data-testid="nc-aplicar-alegra-id"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={submitAplicar}
                  disabled={aplicarBusy || motivo.trim().length < 5}
                  data-testid="nc-aplicar-submit"
                >
                  {aplicarBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Marcar aplicada (solo local)
                </Button>
              </div>
            </details>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAplicarDlg(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="venta" className="mt-4">
          <NotasCreditoVentaTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ───────────────────── helpers UI ─────────────────────

function KpiCard({ label, value, tone = 'gray', numeric = true, testid }) {
  const tones = {
    gray: 'bg-gray-50 text-gray-900',
    amber: 'bg-amber-50 text-amber-900 border-amber-200',
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    indigo: 'bg-indigo-50 text-indigo-900 border-indigo-200',
    green: 'bg-green-50 text-green-900 border-green-200',
    red: 'bg-red-50 text-red-900 border-red-200',
  };
  return (
    <Card className={`${tones[tone]} border`} data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-xs uppercase tracking-wide font-medium opacity-70">
          {label}
        </div>
        <div className={`mt-1 font-bold ${numeric ? 'text-2xl' : 'text-lg'}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function CategoryHelp({ cat }) {
  const meta = CATEGORIA_META[cat];
  if (!meta) return null;
  return (
    <div className={`border rounded p-2 mb-3 text-xs ${meta.cls}`}>
      <span className="mr-1">{meta.icon}</span>
      {meta.desc}
    </div>
  );
}

function NCTable({ items, onLink, onQuitarLink, onAplicar, onRevertir }) {
  if (!items.length) {
    return (
      <div className="text-center py-10 text-gray-500 border rounded">
        No hay notas de crédito en esta categoría.
      </div>
    );
  }
  return (
    <div className="border rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left">
            <th className="px-3 py-2">Folio NC</th>
            <th className="px-3 py-2">Fecha</th>
            <th className="px-3 py-2">Proveedor</th>
            <th className="px-3 py-2 text-right">Monto</th>
            <th className="px-3 py-2">Factura original</th>
            <th className="px-3 py-2">Estado / Razón</th>
            <th className="px-3 py-2 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const meta = CATEGORIA_META[it.categoria] || {};
            return (
              <tr
                key={it.folio + it.emisor_rut}
                className="border-t hover:bg-gray-50"
                data-testid={`nc-row-${it.folio}`}
              >
                <td className="px-3 py-2 font-mono">{it.folio}</td>
                <td className="px-3 py-2 text-gray-600">{fmtDate(it.fecha_emision)}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{it.emisor_razon_social || '—'}</div>
                  <div className="text-xs text-gray-500">{it.emisor_rut}</div>
                </td>
                <td className="px-3 py-2 text-right font-semibold text-orange-700">
                  {fmtCLP(it.total)}
                </td>
                <td className="px-3 py-2">
                  {it.folio_factura_ref ? (
                    <div>
                      <span className="font-mono text-blue-700">
                        {it.folio_factura_ref}
                      </span>
                      {it.factura_original && (
                        <div className="text-xs text-gray-500">
                          {fmtDate(it.factura_original.fecha_emision)} ·{' '}
                          {fmtCLP(
                            it.factura_original.totales?.total || it.factura_original.total,
                          )}
                          {it.factura_original.alegra_bill_id && (
                            <a
                              href={`https://app.alegra.com/bill/view/id/${it.factura_original.alegra_bill_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-1 text-purple-600 hover:underline inline-flex items-center"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      )}
                      {(it.nc_oc_aplicada_a || it.nc_alegra_debit_note_id) && (
                        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap" data-testid={`nc-traza-${it.folio}`}>
                          {it.nc_oc_aplicada_a && (
                            <span className="inline-flex items-center gap-0.5">
                              <FileText className="w-3 h-3 text-orange-600" />
                              OC <b className="font-mono">{it.nc_oc_aplicada_a}</b>
                            </span>
                          )}
                          {it.nc_alegra_debit_note_id && (
                            <span className="inline-flex items-center gap-0.5 text-emerald-700">
                              ND Alegra <b className="font-mono">{it.nc_alegra_debit_note_id}</b>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 italic">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Badge className={meta.cls || ''}>
                    {meta.icon} {meta.label || it.categoria}
                  </Badge>
                  {it.razon_xml && (
                    <div className="text-xs text-gray-500 mt-1 italic">
                      {it.razon_xml}
                    </div>
                  )}
                  {it.nc_aplicada_motivo && (
                    <div className="text-xs text-gray-600 mt-1">
                      {it.nc_aplicada_motivo}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1 flex-wrap">
                    {!it.nc_aplicada && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onLink(it)}
                        data-testid={`nc-link-btn-${it.folio}`}
                      >
                        <Link2 className="w-3 h-3 mr-1" />
                        Linkear
                      </Button>
                    )}
                    {it.link_origen === 'manual' && !it.nc_aplicada && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onQuitarLink(it.folio)}
                        data-testid={`nc-unlink-btn-${it.folio}`}
                      >
                        <Unlink className="w-3 h-3" />
                      </Button>
                    )}
                    {!it.nc_aplicada && it.folio_factura_ref && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onAplicar(it)}
                        data-testid={`nc-aplicar-btn-${it.folio}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Aplicar
                      </Button>
                    )}
                    {it.nc_aplicada && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRevertir(it.folio)}
                        data-testid={`nc-revertir-btn-${it.folio}`}
                      >
                        <Undo2 className="w-3 h-3 mr-1" />
                        Revertir
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
